import { Product } from './types';
import { CandleProduct } from '../types';

/**
 * Converts a shared Product interface into a CandleProduct for the website components.
 * Maintains complete backwards compatibility while powering live updates.
 */
export function productToCandleProduct(p: Product): CandleProduct {
  // Find cover image, or first ordered image, or fallback
  const sortedImages = p.images ? [...p.images].sort((a, b) => a.order - b.order) : [];
  const coverImageObj = sortedImages.find((img) => img.cover) || sortedImages[0];
  const coverUrl = coverImageObj?.url || p.productImage || 'https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format,compress&fit=crop&w=800&q=75';

  const finalPrice = p.salePrice !== undefined && p.salePrice > 0 ? p.salePrice : p.price;

  // Extract top/heart/base notes
  let top = p.topNotes;
  let heart = p.heartNotes;
  let base = p.baseNotes;

  if (!top || top.length === 0) {
    if (p.notes && p.notes.includes('|')) {
      const parts = p.notes.split('|');
      top = parts[0] ? parts[0].split(',').map((s) => s.trim()).filter(Boolean) : [];
      heart = parts[1] ? parts[1].split(',').map((s) => s.trim()).filter(Boolean) : [];
      base = parts[2] ? parts[2].split(',').map((s) => s.trim()).filter(Boolean) : [];
    } else if (p.notes) {
      top = p.notes.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }

  return {
    id: p.id,
    name: p.name,
    tagline: p.shortDescription || p.tagline || p.description || 'Handcrafted luxury botanical candle.',
    description: p.fullDescription || p.description || '',
    topNotes: top && top.length > 0 ? top : ['Crushed Cardamom', 'Violet Leaf'],
    heartNotes: heart && heart.length > 0 ? heart : ['Australian Sandalwood', 'Papyrus'],
    baseNotes: base && base.length > 0 ? base : ['Amber Resin', 'Cedarwood'],
    burnTime: p.burnTime || '55 - 60 Hours',
    weight: p.weight || '280g / 9.8 oz',
    waxType: p.waxType || '100% Pure Soy & Coconut Wax Blend',
    wickType: p.wickType || 'Unbleached Organic Cotton Wick',
    mood: p.mood || p.category || 'Luxury Serenity',
    price: finalPrice,
    colorHex: p.colorHex || '#d4a373',
    flameColorHex: p.flameColorHex || '#ffaa33',
    accentColor: p.accentColor || 'from-amber-700 to-amber-900',
    imageBg: p.imageBg || 'linear-gradient(135deg, #2b1e17 0%, #17100b 100%)',
    vibeStory: p.vibeStory || p.fullDescription || p.description || '',
    productImage: coverUrl,
  };
}

/**
 * Calculates stock status display label
 */
export function getStockStatus(stock: number): {
  label: 'In Stock' | 'Low Stock' | 'Out of Stock';
  badgeClass: string;
} {
  if (stock <= 0) {
    return { label: 'Out of Stock', badgeClass: 'bg-red-100 text-red-800 border-red-200' };
  }
  if (stock <= 10) {
    return { label: 'Low Stock', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200' };
  }
  return { label: 'In Stock', badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
}
